import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import HomeSvg from 'bitmovin-web-ui/src/media/icons/ui/home.svg';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables/index';
import React, {Fragment} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';
import Sugar from 'sugar';

import {Product} from '../../../data/product';
import {SalesDemo, SalesDemoCategory} from '../../../logic/api/frontendService/entities';
import {salesDemoCategoryToHeader} from '../../../logic/humanizer';
import {RootState} from '../../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../../logic/store/salesDemosStore';
import ChevronLineStrokeDownSvg from '../../../media/chevron_line_stroke_down.svg';
import DemosSvg from '../../../media/demos.svg';
import PersonSvg from '../../../media/person.svg';
import HeaderBackgroundSvg from '../../../media/quickSwitch/header_background.svg';
import {maxExtendedWidth, pxToEm} from '../../globalStyles';

import ProductTileLink from './productTileLink';
import SalesDemoTileLink from './salesDemoTileLink';

//#region Styled Components

const StyledIconButton = styled(Button)`
  display: inline-flex !important;
  align-items: center;
  justify-content: center;

  > img {
    line-height: 1em;
    &:first-child {
      margin-right: 0.85em;
    }
    &:last-child {
      margin-left: 0.85em;
    }
  }
`;

const StyledBlueIconButton = styled(StyledIconButton)`
  background: linear-gradient(90deg, #1ab3ff 0%, #006bff 97.47%);
`;

const QUICK_SWITCH_WIDTH_PX = 1016;
const QUICK_SWITCH_PADDING_PX = 20;
const QUICK_SWITCH_PADDING_SMALL_SCREEN_PX = 10;
const QUICK_SWITCH_SHADOW = '0 0 29px rgba(0, 0, 0, 0.25)';
const QUICK_SWITCH_DROPDOWN_Z_INDEX = 5;

const StyledDropdownContainer = styled.div`
  display: none;
  flex-direction: column;
  position: absolute;
  right: 0;
  z-index: ${QUICK_SWITCH_DROPDOWN_Z_INDEX};
  width: ${pxToEm(QUICK_SWITCH_WIDTH_PX)}em;
  padding-top: 0.3em;
`;

const StyledQuickSwitchButton = styled(StyledBlueIconButton)`
  > img {
    height: ${pxToEm(8)}em;
    transition: transform 0.25s ease-in-out;
    transform: rotate(90deg);
  }
`;

const StyledWrapperDiv = styled.div<{isOpen: boolean}>`
  display: inline-block;
  position: relative;

  > ${StyledQuickSwitchButton} > img {
    transform: ${props => (props.isOpen ? 'none' : 'rotate(90deg)')};
  }

  > ${StyledDropdownContainer} {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
  }
`;

const StyledDropdownBackdropDiv = styled.div`
  position: fixed;
  left: 0;
  z-index: ${QUICK_SWITCH_DROPDOWN_Z_INDEX - 1};
  width: 100%;
  height: 100%;
`;

const StyledHeaderWithProductsDiv = styled.div`
  position: relative;

  > img {
    width: 100%;
    display: block;
    filter: drop-shadow(${QUICK_SWITCH_SHADOW});
  }

  > div {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: ${pxToEm(QUICK_SWITCH_PADDING_PX)}em;

    @media (max-width: ${maxExtendedWidth}) {
      padding: ${pxToEm(QUICK_SWITCH_PADDING_PX)}em ${pxToEm(QUICK_SWITCH_PADDING_SMALL_SCREEN_PX)}em;
    }
  }
`;

const StyledProductsTileLinksContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: ${pxToEm(25)}em;
`;

const StyledCategoryHeadline = styled.h1`
  font-size: ${pxToEm(20)}em;
  line-height: 1.3;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 0.8em 0;

  @media (max-width: ${maxExtendedWidth}) {
    margin: 0.4em 0;
  }
`;

const StyledProductsHeadline = styled(StyledCategoryHeadline)`
  color: ${baseVariables.colors.white};
`;

const StyledSeparator = styled.div`
  height: ${pxToEm(3)}em;
  background: linear-gradient(180deg, #ffb31a 0%, #ff6b00 100%);
`;

const StyledMainWhiteDiv = styled.div`
  background-color: ${baseVariables.colors.white};
  box-shadow: ${QUICK_SWITCH_SHADOW};
  border-bottom-left-radius: ${baseVariables.sizes.borderRadius};
  border-bottom-right-radius: ${baseVariables.sizes.borderRadius};
`;

const StyledScrollableContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 ${pxToEm(QUICK_SWITCH_PADDING_PX)}em;

  @media (max-width: ${maxExtendedWidth}) {
    padding: 0 ${pxToEm(QUICK_SWITCH_PADDING_SMALL_SCREEN_PX)}em;
  }
`;

const StyledSalesDemosCategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: ${pxToEm(12)}em;
`;

const StyledLinkButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${pxToEm(QUICK_SWITCH_PADDING_PX)}em;

  @media (max-width: ${maxExtendedWidth}) {
    padding: ${pxToEm(QUICK_SWITCH_PADDING_SMALL_SCREEN_PX)}em;
  }
`;

const StyledAnchor = styled.a`
  &:not(:last-child) {
    margin-right: 1em;
  }
  > ${StyledIconButton} {
    font-size: ${pxToEm(18)}em !important;
  }
`;

//#endregion

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {
  className?: string;
}

interface State {
  isOpen: boolean;
}

class QuickSwitch extends React.Component<Props, State> {
  public state: Readonly<State> = {
    isOpen: false
  };

  private handleQuickSwitchButtonClick() {
    const {loadingState} = this.props.salesDemos;
    if (loadingState !== LoadingState.Success) {
      return;
    }

    this.toggleOpenedState();
  }

  private toggleOpenedState() {
    this.setState(predState => ({isOpen: !predState.isOpen}));
  }

  public render() {
    const {loadingState} = this.props.salesDemos;
    const areSalesDemosLoading = loadingState !== LoadingState.Success;

    return (
      <StyledWrapperDiv isOpen={this.state.isOpen}>
        <StyledQuickSwitchButton
          className={this.props.className}
          variant={ButtonVariant.FilledBlue}
          disabled={areSalesDemosLoading}
          onClick={() => this.handleQuickSwitchButtonClick()}>
          <span>Quick Switch</span>
          <img src={ChevronLineStrokeDownSvg} alt="chevron" />
        </StyledQuickSwitchButton>
        {!areSalesDemosLoading && this.renderDropdownContainer()}

        {this.state.isOpen && <StyledDropdownBackdropDiv onClick={() => this.toggleOpenedState()} />}
      </StyledWrapperDiv>
    );
  }

  private renderDropdownContainer() {
    const allSalesDemos = this.props.salesDemos.response.demos || [];
    const featuresSalesDemos = allSalesDemos.filter(salesDemo => salesDemo.category !== SalesDemoCategory.EMBEDDED);
    const sortedFeaturesSalesDemos = Sugar.Array.sortBy(featuresSalesDemos, salesDemo => salesDemo.name);

    return (
      <StyledDropdownContainer>
        <StyledHeaderWithProductsDiv>
          <img src={HeaderBackgroundSvg} alt="dropdown header background" />
          <div>
            <StyledProductsHeadline>Products</StyledProductsHeadline>
            <StyledProductsTileLinksContainer>
              <ProductTileLink product={Product.Encoding} />
              <ProductTileLink product={Product.LiveEncoding} />
              <ProductTileLink product={Product.Playback} />
            </StyledProductsTileLinksContainer>
          </div>
        </StyledHeaderWithProductsDiv>

        <StyledSeparator />

        <StyledMainWhiteDiv>
          <StyledScrollableContainer>
            {this.renderSalesDemosCategory(sortedFeaturesSalesDemos, SalesDemoCategory.HIGHER_QUALITY)}
            {this.renderSalesDemosCategory(sortedFeaturesSalesDemos, SalesDemoCategory.EASE_OF_USE)}
            {this.renderSalesDemosCategory(sortedFeaturesSalesDemos, SalesDemoCategory.INNOVATION)}
            {this.renderSalesDemosCategory(sortedFeaturesSalesDemos, SalesDemoCategory.PARTNERS)}
            {this.renderSalesDemosCategory(sortedFeaturesSalesDemos, SalesDemoCategory.MONETIZATION)}
          </StyledScrollableContainer>

          <StyledLinkButtonsContainer>
            <StyledAnchor href="https://bitmovin.com/demos" target="_blank">
              <StyledBlueIconButton variant={ButtonVariant.FilledBlue}>
                <span>Demos Homepage</span>
                <img src={DemosSvg} alt="demos" />
              </StyledBlueIconButton>
            </StyledAnchor>

            <div>
              <StyledAnchor href="https://dashboard.bitmovin.com" target="_blank">
                <StyledIconButton variant={ButtonVariant.OutlinedLight}>
                  <img src={PersonSvg} alt="person" />
                  <span>Dashboard</span>
                </StyledIconButton>
              </StyledAnchor>
              <StyledAnchor href="https://bitmovin.com" target="_blank">
                <StyledIconButton variant={ButtonVariant.OutlinedLight}>
                  <img src={HomeSvg} alt="home" />
                  <span>bitmovin.com</span>
                </StyledIconButton>
              </StyledAnchor>
            </div>
          </StyledLinkButtonsContainer>
        </StyledMainWhiteDiv>
      </StyledDropdownContainer>
    );
  }

  private renderSalesDemosCategory(salesDemos: SalesDemo[], category: SalesDemoCategory) {
    const categorySalesDemos = salesDemos.filter(salesDemo => salesDemo.category === category);

    if (categorySalesDemos.length === 0) {
      return null;
    }

    const salesDemoTileLinks = categorySalesDemos.map(salesDemo => (
      <SalesDemoTileLink key={salesDemo.id} salesDemo={salesDemo} />
    ));

    return (
      <Fragment>
        <StyledCategoryHeadline>{salesDemoCategoryToHeader(category)}</StyledCategoryHeadline>
        <StyledSalesDemosCategoryGrid>{salesDemoTileLinks}</StyledSalesDemosCategoryGrid>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(QuickSwitch);
